// The chips of a card: When, Repeat, Reminder, Deadline, Tags and Checklist, always in that order (docs/55, item 16):
// a set one is its chip, an unset one its quiet tool, both the same button, so nothing moves under the pointer as a
// value is set and the keyboard's focus comes back to it after a choice. Detected and link chips follow them. As
// the open card in the apps has them (TodoEditor.swift, MacTodoProperties.swift).

import { useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { destinationOf, movedTodo, noteSomeday, type MoveDestination } from "../core/actions";
import { alignStartWithRepeat, reminderMoment, removeScopedReminder, setScopedReminder, type ReminderScope } from "../core/repeats";
import { reminderOnDay } from "./quickEntry";
import type { Area, Todo } from "../core/records";
import type { Day, RepeatRule, Start } from "../core/types";
import { t } from "../i18n";
import { useWindow } from "./context";
import { deadlineChipLabel, repeatLabel, timeLabel, whenIcon, whenLabel } from "./format";
import { Icon, type IconName } from "./Icon";
import { Popover } from "./Menu";
import { DeadlinePicker, MovePicker, ReminderPicker, RepeatPicker, TagPicker, WhenPicker } from "./Pickers";

export type Picker = "when" | "deadline" | "reminder" | "repeat" | "tags" | "move" | "convert" | "todaySection";

export interface PropertyChange {
  start?: Start;
  deadline?: Day | null;
  reminder?: { hour: number; minute: number } | null;
  /** Which reminders of a repeating to-do `reminder` is for (docs/62): this one (the default) or all future ones too. */
  reminderScope?: ReminderScope;
  repeats?: RepeatRule | null;
  /** The day the start is aligned with the repeat from: the start the Repeat picker opened with (docs/67). */
  repeatOrigin?: Day;
  toggleTag?: string;
  move?: MoveDestination;
  skip?: boolean;
  /** Convert to Project, in this area (the to-do had none of its own, so one was chosen). */
  convertIn?: string;
  /** Into this Today section, or out of all of them (null): Today Section, as in the iPhone's editor and the Mac's menu. */
  todaySection?: string | null;
}

export function usePicker() {
  const anchor = useRef<HTMLElement | null>(null);
  /** The slot the picker was opened from (`data-slot`), where the focus returns after a choice. */
  const slot = useRef<string | null>(null);
  const [open, setOpen] = useState<Picker | null>(null);
  /** The focus's way back after a close, still to come in the next frame. */
  const returning = useRef<number | null>(null);
  const cancelReturn = () => {
    if (returning.current !== null) cancelAnimationFrame(returning.current);
    returning.current = null;
  };
  const show = (picker: Picker, element: HTMLElement) => {
    // A picker opened again before the last one's focus came back (Esc, then Return at once) keeps the focus in its
    // field: the frame would have taken it to the chip, and Return with it.
    cancelReturn();
    anchor.current = element;
    slot.current = element.dataset.slot ?? null;
    setOpen(picker);
  };
  const close = () => {
    const returnTo = slot.current;
    const row = anchor.current?.closest(".chips");
    setOpen(null);
    if (!returnTo || !row) return;
    // Back to the chip, or to what took its place (the tool became a chip, the chip a tool): after the popover is
    // gone, and only when the focus has nowhere else to be (a click elsewhere keeps its own focus).
    cancelReturn();
    returning.current = requestAnimationFrame(() => {
      returning.current = null;
      const active = document.activeElement;
      if (active && active !== document.body && active.isConnected && !active.closest(".popover")) return;
      row.querySelector<HTMLElement>(`[data-slot="${returnTo}"]`)?.focus({ preventScroll: true });
    });
  };
  return { anchor, open, show, close };
}

/** One property's button: its chip while set, its quiet tool while not; the same element either way. */
function Property({ slot, set, icon, label, title, hint, red, today, onClick }: {
  slot: string; set: boolean; icon: IconName; label?: string; title: string; hint?: string; red?: boolean; today?: boolean; onClick: (element: HTMLElement) => void;
}) {
  return (
    <button data-slot={slot} className={set ? `chip${red ? " red" : ""}${today ? " today" : ""}` : "tool"} title={hint ? `${title} (${hint})` : title}
      aria-label={set && label ? `${title}: ${label}` : title} onClick={(event) => onClick(event.currentTarget)}>
      <Icon name={icon} />{set && label}
    </button>
  );
}

/** ← → move between the chips and tools; Tab does too, as the row is a run of buttons (docs/55, item 14). */
function moveAlong(event: KeyboardEvent<HTMLDivElement>) {
  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
  const target = event.target as HTMLElement;
  if (target.matches("input, textarea") || target.closest(".popover")) return;
  const buttons = [...event.currentTarget.querySelectorAll<HTMLElement>("button:not([disabled])")].filter((button) => !button.closest(".popover"));
  const index = buttons.indexOf(target);
  if (index < 0) return;
  const next = buttons[index + (event.key === "ArrowRight" ? 1 : -1)];
  if (!next) return;
  event.preventDefault();
  next.focus();
}

/** The chips for what is set, the tools for what isn't, and the picker that one of them opened. */
export function Properties({ todo, picker, onChange, before, tools, onChecklist, convertAreas }: {
  todo: Todo;
  picker: ReturnType<typeof usePicker>;
  onChange: (change: PropertyChange) => void;
  /** Chips that follow the fixed ones (Quick Entry's parsed words). */
  before?: ReactNode;
  tools: ("when" | "deadline" | "reminder" | "repeat" | "tags" | "checklist" | "move")[];
  onChecklist?: () => void;
  /** The areas Convert to Project offers, when the to-do has none of its own (core/actions: projectAreaChoice). */
  convertAreas?: readonly Area[];
}) {
  const { state } = useWindow();
  const { today, zone } = state;
  const when = whenLabel(todo.start, today);
  const allTags = [...new Set(state.records.todos.flatMap((other) => other.tags))].sort((a, b) => a.localeCompare(b));

  const has = (tool: (typeof tools)[number]) => tools.includes(tool);
  const reminder = todo.reminder ? timeLabel(reminderMoment(todo, zone) ?? todo.reminder, zone) : undefined;

  return (
    <div className="chips" onKeyDown={moveAlong}>
      {(has("when") || when) && (
        <Property slot="when" set={when !== null} icon={when ? whenIcon(todo.start, today) : "calendar"} label={when ?? undefined} title={t("When")} hint="W"
          today={todo.start.kind === "on" && todo.start.day <= today}
          onClick={(element) => picker.show("when", element)} />
      )}
      {(has("repeat") || todo.repeats) && (
        <Property slot="repeat" set={todo.repeats !== undefined} icon="repeat" label={todo.repeats && repeatLabel(todo.repeats)} title={t("Repeat")}
          onClick={(element) => picker.show("repeat", element)} />
      )}
      {(has("reminder") || todo.reminder) && (
        <Property slot="reminder" set={todo.reminder !== undefined} icon={todo.reminder ? "bellFill" : "bell"} label={reminder} title={t("Reminder")}
          onClick={(element) => picker.show("reminder", element)} />
      )}
      {(has("deadline") || todo.deadline) && (
        <Property slot="deadline" set={todo.deadline !== undefined} icon="due" label={todo.deadline && deadlineChipLabel(todo.deadline, today)}
          red={todo.deadline !== undefined && todo.deadline <= today} title={t("Deadline")} hint="D" onClick={(element) => picker.show("deadline", element)} />
      )}
      {todo.tags.length > 0
        ? todo.tags.map((tag) => <Property key={tag} slot="tags" set icon="tag" label={tag} title={t("Tags")} onClick={(element) => picker.show("tags", element)} />)
        : has("tags") && <Property slot="tags" set={false} icon="tag" title={t("Tags")} onClick={(element) => picker.show("tags", element)} />}
      {has("checklist") && todo.checklist.length === 0 && onChecklist && (
        <Property slot="checklist" set={false} icon="checklist" title={t("Checklist")} onClick={() => onChecklist()} />
      )}
      {has("move") && <Property slot="move" set={false} icon="move" title={t("Move")} onClick={(element) => picker.show("move", element)} />}
      {before}

      {picker.open && (
        <Popover anchor={picker.anchor} onClose={picker.close} label={t(LABELS[picker.open])} className={picker.open === "todaySection" ? "menu" : ""}>
          {picker.open === "when" && (
            <WhenPicker start={todo.start} today={today} onChoose={(start) => { onChange({ start }); picker.close(); }}
              onRepeat={(rule, day) => { onChange({ start: { kind: "on", day }, repeats: rule }); picker.close(); }} />
          )}
          {picker.open === "deadline" && <DeadlinePicker deadline={todo.deadline} today={today} onChoose={(deadline) => { onChange({ deadline: deadline ?? null }); picker.close(); }} />}
          {picker.open === "reminder" && <ReminderPicker todo={todo} today={today} zone={zone} onKeep={picker.close} onChoose={(time, scope) => { onChange({ reminder: time ?? null, reminderScope: scope }); picker.close(); }} />}
          {picker.open === "repeat" && (
            <RepeatPicker todo={todo} today={today} onChange={(rule, origin) => onChange({ repeats: rule ?? null, ...(origin ? { repeatOrigin: origin } : {}) })}
              onStart={(day) => onChange({ start: { kind: "on", day } })}
              onSkip={todo.completedAt === undefined ? () => { onChange({ skip: true }); picker.close(); } : undefined} />
          )}
          {picker.open === "tags" && <TagPicker tags={todo.tags} allTags={allTags} onToggle={(tag) => onChange({ toggleTag: tag })} />}
          {picker.open === "move" && (
            <MovePicker areas={state.records.areas} projects={state.records.projects} current={destinationOf(todo)} progress={(project) => state.lists.progress(project)}
              onChoose={(move) => { onChange({ move }); picker.close(); }} onClose={picker.close} />
          )}
          {picker.open === "todaySection" && (
            <div role="menu">
              {[{ id: null, title: t("No Section") }, ...state.lists.todaySections.map((section) => ({ id: section.id as string | null, title: section.title }))].map((option) => (
                <button key={option.id ?? "none"} role="menuitemcheckbox" aria-checked={(todo.todaySectionID ?? null) === option.id}
                  onClick={() => { onChange({ todaySection: option.id }); picker.close(); }}>
                  <span>{option.title}</span>
                  {(todo.todaySectionID ?? null) === option.id && <Icon name="check" className="menu-tick" />}
                </button>
              ))}
            </div>
          )}
          {picker.open === "convert" && (
            <MovePicker areasOnly areas={convertAreas ?? []} projects={[]} progress={(project) => state.lists.progress(project)}
              onChoose={(destination) => { picker.close(); if (destination.kind === "area") onChange({ convertIn: destination.id }); }} onClose={picker.close} />
          )}
        </Popover>
      )}
    </div>
  );
}

const LABELS: Record<Picker, string> = {
  when: "When", deadline: "Deadline", reminder: "Reminder", repeat: "Repeat", tags: "Tags", move: "Move", convert: "Convert to Project", todaySection: "Today Section",
};

/** The to-do with a picker's change (the reminder keeps to the to-do's day; a new day takes the reminder along). */
export function applyChange(todo: Todo, change: PropertyChange, today: Day, zone: string): Todo {
  let next: Todo = { ...todo };
  if (change.start) {
    next.start = change.start;
    // The reminder rides the new day; Someday takes it away (core/actions: noteSomeday), Anytime keeps it, as in the apps.
    if (next.reminder && change.start.kind === "on") next.reminder = reminderOnDay(next, change.start.day, zone) ?? next.reminder;
    next = noteSomeday(todo, next);
  }
  if (change.deadline !== undefined) {
    if (change.deadline === null) delete next.deadline;
    else next.deadline = change.deadline;
  }
  if (change.reminder !== undefined) {
    const scope = change.reminderScope ?? "this";
    if (change.reminder === null) removeScopedReminder(next, scope);
    else setScopedReminder(next, change.reminder.hour, change.reminder.minute, scope, today, zone);
  }
  if (change.repeats !== undefined) {
    if (change.repeats === null) delete next.repeats;
    else {
      next.repeats = change.repeats;
      next = alignStartWithRepeat(next, today, change.repeatOrigin);
    }
  }
  if (change.toggleTag) {
    const tag = change.toggleTag;
    next.tags = next.tags.includes(tag) ? next.tags.filter((other) => other !== tag) : [...next.tags, tag];
  }
  if (change.move) next = movedTodo(next, change.move);
  return next;
}
